<template>
  <section v-show="active" class="app-modal__section">
    <header class="app-modal__section-head app-modal__section-head--split">
      <div class="payment-section-head-copy">
        <h4>{{ text.heading.title }}</h4>
        <p>{{ text.heading.description }}</p>
      </div>

      <div class="payment-readonly-status" role="status" aria-live="polite">
        <strong>{{ text.status.prefix }}: {{ pendingStatusLabel.toLowerCase() }}</strong>
        <small>{{ text.status.autoHelp }}</small>
      </div>
    </header>

    <div class="app-modal__grid two-columns">
      <div class="app-modal-field">
        <span>{{ text.fields.documentType }}</span>
        <multiselect
          v-model="voucherTypeModel"
          :options="options.documentTypes"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="false"
          :allow-empty="false"
          :disabled="!canEditGeneralFields"
          class="app-modal-select"
        >
          <template #noResult><span>{{ commonText.noOption }}</span></template>
          <template #noOptions><span>{{ commonText.noOption }}</span></template>
        </multiselect>
      </div>

      <div v-if="showCustomerSelector" class="app-modal-field">
        <span>{{ text.fields.customer }}</span>
        <multiselect
          v-model="customerModel"
          :options="customerOptions"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="false"
          :disabled="!canEditGeneralFields"
          class="app-modal-select"
          :placeholder="text.fields.customerPlaceholder"
        >
          <template #noResult><span>{{ commonText.noOption }}</span></template>
          <template #noOptions><span>{{ commonText.noOption }}</span></template>
        </multiselect>
      </div>
    </div>

    <section class="expense-modal-box">
      <header class="expense-modal-box__head">
        <div>
          <h4>{{ text.fields.paymentAmountsTitle }}</h4>
          <p>{{ text.fields.paymentAmountsSubtitle }}</p>
        </div>
        <AppCurrencyControl
          v-model="currencyModel"
          :disabled="!canEditGeneralFields"
          :exchange-rate="form.exchangeRate"
          @update:exchange-rate="form.exchangeRate = $event"
        />
      </header>

      <div v-if="!isEditMode" class="payment-frequency-box">
        <span class="payment-frequency-box__label">{{ text.fields.frequencyLabel }}</span>
        <div class="billing-status-row">
          <label
            v-for="option in frequencyCheckboxOptions"
            :key="option.value"
            :class="[
              'app-modal-toggle',
              'billing-toggle',
              { 'app-modal-toggle--disabled': !canEditGeneralFields }
            ]"
          >
            <input
              type="checkbox"
              :checked="selectedFrequency === option.value"
              :disabled="!canEditGeneralFields"
              @change="selectFrequency(option.value)"
            />
            <span>{{ option.label }}</span>
          </label>
        </div>

        <div v-if="showRecurringFields" class="app-modal__grid two-columns payment-recurring-grid">
          <label class="app-modal-field payment-recurring-count">
            <span>{{ text.fields.recurringCount }}</span>
            <input
              v-model.number="form.paymentsCount"
              type="number"
              min="1"
              step="1"
              :disabled="!canEditGeneralFields"
              class="form-control payment-recurring-count__input"
              :class="{ 'is-invalid': hasFieldError('paymentsCount') }"
              :placeholder="text.fields.recurringCountPlaceholder"
              @blur="touchField('paymentsCount')"
            />
            <div class="invalid-feedback d-block" v-if="hasFieldError('paymentsCount')">
              <span v-if="v$.form.paymentsCount.$errors.length">
                {{ v$.form.paymentsCount.$errors[0].$message }}
              </span>
            </div>
          </label>

          <div v-if="showMonthlyFields" class="app-modal-field">
            <span>{{ text.fields.firstInstallment }}</span>
            <multiselect
              ref="firstInstallmentSelect"
              v-model="form.firstInstallmentOption"
              :options="firstInstallmentOptions"
              track-by="value"
              label="label"
              :show-labels="false"
              :searchable="true"
              :allow-empty="true"
              :disabled="!canEditGeneralFields"
              :max-height="152"
              open-direction="down"
              :class="['app-modal-select', { 'is-invalid': hasFieldError('firstInstallmentOption') }]"
              :placeholder="text.fields.firstInstallmentPlaceholder"
              @open="focusSelectedFirstInstallment"
              @close="touchField('firstInstallmentOption')"
            >
              <template #noResult><span>{{ commonText.noOption }}</span></template>
              <template #noOptions><span>{{ commonText.noOption }}</span></template>
            </multiselect>
            <div class="invalid-feedback d-block" v-if="hasFieldError('firstInstallmentOption')">
              <span v-if="v$.form.firstInstallmentOption.$errors.length">
                {{ v$.form.firstInstallmentOption.$errors[0].$message }}
              </span>
            </div>
          </div>

          <div v-if="showMultipleFields" class="app-modal-field">
            <span>{{ text.fields.periodicity }}</span>
            <multiselect
              v-model="form.periodicityOption"
              :options="options.periodicities"
              track-by="value"
              label="label"
              :show-labels="false"
              :searchable="true"
              :allow-empty="true"
              :disabled="!canEditGeneralFields"
              :class="['app-modal-select', { 'is-invalid': hasFieldError('periodicityOption') }]"
              :placeholder="text.fields.periodicityPlaceholder"
              @close="touchField('periodicityOption')"
            >
              <template #noResult><span>{{ commonText.noOption }}</span></template>
              <template #noOptions><span>{{ commonText.noOption }}</span></template>
            </multiselect>
            <div class="invalid-feedback d-block" v-if="hasFieldError('periodicityOption')">
              <span v-if="v$.form.periodicityOption.$errors.length">
                {{ v$.form.periodicityOption.$errors[0].$message }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="app-modal__grid two-columns">
        <label class="app-modal-field">
          <span>{{ text.fields.subtotal }}</span>
          <AppMoneyInput
            v-model="form.amount"
            output-mode="number"
            :decimal-places="2"
            :minimum-value="0"
            :currency="currencyModel"
            :disabled="!canEditAmount"
          />
          <div class="invalid-feedback d-block" v-if="hasFieldError('amount')">
            <span v-if="v$.form.amount.$errors.length">
              {{ v$.form.amount.$errors[0].$message }}
            </span>
          </div>
          <small
            v-if="showPendingWarnings"
            class="expense-field-warning"
            :class="{ 'expense-field-warning--overrun': hasExceededPending }"
          >
            {{ pendingAmountWarning }}
          </small>
        </label>

        <label class="app-modal-field">
          <span>{{ text.fields.percent }}</span>
          <AppMoneyInput
            v-model="percentageValue"
            output-mode="number"
            :decimal-places="2"
            :minimum-value="0"
            :show-currency-symbol="true"
            currency-symbol="%"
            :disabled="!canEditAmount"
            @change="applyPercentageToAmount"
          />
          <small
            v-if="showPendingWarnings"
            class="expense-field-warning"
            :class="{ 'expense-field-warning--overrun': hasExceededPending }"
          >
            {{ pendingPercentWarning }}
          </small>
        </label>
      </div>

      <AppGeneralTaxesBlock
        :show-taxes-section="showTaxesSection"
        :show-tax-selectors="showTaxSelectors"
        :without-taxes-enabled="withoutTaxesEnabled"
        :disabled="!canEditGeneralFields"
        :form="form"
        :options="options"
        :text="taxesText"
        :common-text="taxesCommonText"
        @toggle-without-taxes="toggleWithoutTaxes"
      />

      <AppGeneralTotalsSummary
        :tax-preview="taxPreview"
        :show-tax-breakdown-summary="showTaxBreakdownSummary"
        :show-tax-section-breakdown="showTaxSectionBreakdown"
        :transferred-tax-items="transferredTaxItems"
        :withheld-tax-items="withheldTaxItems"
        :format-money="formatMoney"
        :format-tax-breakdown-line="formatTaxBreakdownLine"
      />
    </section>

    <PaymentPlanGeneralScheduleBlock
      :form="form"
      :options="options"
      :first-installment-options="firstInstallmentOptions"
      :show-invoice-date="!isSimpleNoteVoucher"
      :can-edit-expense-date="canEditGeneralFields"
      :can-edit-payment-date="canEditPaymentDate"
      :can-edit-concept="canEditGeneralFields"
      :has-field-error="hasFieldError"
      :touch-field="touchField"
      :v$="v$"
    />
  </section>
</template>

<script>
import AppMoneyInput from '@/components/common/AppMoneyInput.vue';
import AppCurrencyControl from '@/components/common/AppCurrencyControl.vue';
import AppGeneralTaxesBlock from '@/components/common/AppGeneralTaxesBlock.vue';
import AppGeneralTotalsSummary from '@/components/common/AppGeneralTotalsSummary.vue';
import PaymentPlanGeneralScheduleBlock from '@/components/sales/shared/paymentPlan/modal/PaymentPlanModalSections/PaymentPlanGeneralScheduleBlock.vue';
import {
  formatPaymentPlanMoney,
  formatPaymentPlanTaxBreakdownLine,
} from '@/composables/paymentPlans/usePaymentPlanTaxFlow';
import { resolveExpenseFeeVoucherFlags } from '@/composables/expensesFees/shared/useExpenseFeeTaxFlow';
import {
  applyFrequencyRules,
  applyVoucherTypeRules,
  applyWithoutTaxesRules,
} from '@/composables/paymentPlans/usePaymentPlanGeneralTransitions';
import { PAYMENTS_TEXT } from '@/texts/catalog/payments';

const GENERAL_TEXT = PAYMENTS_TEXT.planModal.general;
const COMMON_TEXT = PAYMENTS_TEXT.planModal.common;

export default {
  name: 'PaymentPlanModalSectionGeneral',
  components: {
    AppMoneyInput,
    AppCurrencyControl,
    AppGeneralTaxesBlock,
    AppGeneralTotalsSummary,
    PaymentPlanGeneralScheduleBlock,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
    showCustomerSelector: {
      type: Boolean,
      default: false,
    },
    customerOptions: {
      type: Array,
      default: () => [],
    },
    salePrice: {
      type: Number,
      default: 0,
    },
    existingPlannedTotal: {
      type: Number,
      default: 0,
    },
    remainingBalance: {
      type: Number,
      default: null,
    },
    maxAmountPerInstallment: {
      type: Number,
      default: null,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    isHydrating: {
      type: Boolean,
      default: false,
    },
    taxPreview: {
      type: Object,
      required: true,
    },
    taxPreviewLoading: {
      type: Boolean,
      default: false,
    },
    firstInstallmentOptions: {
      type: Array,
      default: () => [],
    },
    warnings: {
      type: Object,
      default: () => ({}),
    },
    editPermissions: {
      type: Object,
      default: () => ({}),
    },
    v$: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      text: GENERAL_TEXT,
      commonText: COMMON_TEXT,
      percentageValue: 0,
      percentageSyncLock: false,
    };
  },
  computed: {
    voucherTypeModel: {
      get() {
        const target = String(this.form?.voucherType || 'cfdi').trim().toLowerCase();
        return (Array.isArray(this.options?.documentTypes) ? this.options.documentTypes : [])
          .find((option) => String(option?.value || '').trim().toLowerCase() === target)
          || this.options?.documentTypes?.[0]
          || null;
      },
      set(option) {
        this.form.voucherType = String(option?.value || 'cfdi').trim().toLowerCase();
      },
    },
    customerModel: {
      get() {
        const selectedValue = String(this.form?.customer?.value || this.form?.customer?.id || '').trim();
        if (selectedValue === '') {
          return null;
        }

        const options = Array.isArray(this.customerOptions) ? this.customerOptions : [];
        return options.find((option) => String(option?.value || '').trim() === selectedValue)
          || this.form.customer
          || null;
      },
      set(option) {
        this.form.customer = option && typeof option === 'object' ? option : null;
      },
    },
    currencyModel: {
      get() {
        const current = String(this.form?.currencyCode || '').trim().toUpperCase();
        return current !== '' ? current : 'MXN';
      },
      set(value) {
        this.form.currencyCode = String(value || '').trim().toUpperCase() || 'MXN';
      },
    },
    pendingStatusLabel() {
      const pendingOption = (Array.isArray(this.options?.expenseStatuses) ? this.options.expenseStatuses : [])
        .find((option) => String(option?.value || '').trim().toLowerCase() === 'pending');

      const label = String(pendingOption?.label || '').trim();
      return label !== '' ? label : this.text.status.pendingFallback;
    },
    voucherType() {
      return String(this.form?.voucherType || '').trim().toLowerCase();
    },
    isSimpleNoteVoucher() {
      return resolveExpenseFeeVoucherFlags(this.voucherType).isSimpleNoteVoucher;
    },
    isForeignReceiptVoucher() {
      return resolveExpenseFeeVoucherFlags(this.voucherType).isForeignReceiptVoucher;
    },
    isPayrollVoucher() {
      return resolveExpenseFeeVoucherFlags(this.voucherType).isPayrollVoucher;
    },
    showTaxesSection() {
      return !this.isSimpleNoteVoucher && !this.isPayrollVoucher;
    },
    withoutTaxesEnabled() {
      return Boolean(this.form?.withoutTaxes);
    },
    showTaxSelectors() {
      return this.showTaxesSection && !this.withoutTaxesEnabled;
    },
    taxesText() {
      return {
        title: this.text.taxes.title,
        subtitle: this.text.taxes.subtitle,
        withoutTaxes: this.text.taxes.withoutTaxes,
        taxType: this.text.taxes.taxType,
        taxes: this.text.taxes.taxes,
        placeholders: {
          taxes: this.text.taxes.placeholders.taxes,
          group: this.text.taxes.placeholders.group,
        },
        fallbackTaxLabel: ({ id }) => this.text.taxes.fallbackTaxLabel({ id }),
        fallbackGroupLabel: this.text.taxes.fallbackGroupLabel,
        fallbackIndividualLabel: this.text.taxes.fallbackIndividualLabel,
      };
    },
    taxesCommonText() {
      return {
        noOption: this.commonText.noOption,
      };
    },
    showTaxBreakdownSummary() {
      return !this.isSimpleNoteVoucher && !this.isPayrollVoucher && !this.withoutTaxesEnabled;
    },
    taxBreakdownItems() {
      return Array.isArray(this.taxPreview?.taxes?.items)
        ? this.taxPreview.taxes.items
        : [];
    },
    transferredTaxItems() {
      return this.taxBreakdownItems
        .filter((item) => String(item?.direction || '').trim().toLowerCase() === 'trasladado');
    },
    withheldTaxItems() {
      return this.taxBreakdownItems
        .filter((item) => String(item?.direction || '').trim().toLowerCase() === 'retenido');
    },
    showTaxSectionBreakdown() {
      return String(this.taxPreview?.tax_type || this.form?.taxType || '').trim().toLowerCase() === 'group';
    },
    showRecurringFields() {
      return String(this.form?.frequency || '').trim().toLowerCase() !== 'unique';
    },
    showMonthlyFields() {
      return String(this.form?.frequency || '').trim().toLowerCase() === 'monthly';
    },
    showMultipleFields() {
      return String(this.form?.frequency || '').trim().toLowerCase() === 'multiple';
    },
    selectedFrequency() {
      const frequency = String(this.form?.frequency || '').trim().toLowerCase();
      if (['unique', 'monthly', 'multiple'].includes(frequency)) {
        return frequency;
      }

      return 'unique';
    },
    frequencyCheckboxOptions() {
      return [
        { value: 'unique', label: this.text.frequencyOptions.unique },
        { value: 'monthly', label: this.text.frequencyOptions.monthly },
        { value: 'multiple', label: this.text.frequencyOptions.multiple },
      ];
    },
    resolvedEditPermissions() {
      const source = this.editPermissions && typeof this.editPermissions === 'object'
        ? this.editPermissions
        : {};

      return {
        can_edit_general: source.can_edit_general !== false,
        can_edit_billing: source.can_edit_billing !== false,
        can_edit_amount: source.can_edit_amount !== false,
        can_edit_payment_date: source.can_edit_payment_date !== false,
        can_edit_details: source.can_edit_details !== false,
      };
    },
    canEditGeneralFields() {
      return this.resolvedEditPermissions.can_edit_general;
    },
    canEditAmount() {
      return this.resolvedEditPermissions.can_edit_amount;
    },
    canEditPaymentDate() {
      return this.resolvedEditPermissions.can_edit_payment_date;
    },
    salePriceNumber() {
      return Number(this.salePrice || 0) || 0;
    },
    existingPlannedTotalNumber() {
      return Number(this.existingPlannedTotal || 0) || 0;
    },
    currentAmountNumber() {
      return Number(this.form?.amount || 0) || 0;
    },
    installmentsCount() {
      if (!this.showRecurringFields) {
        return 1;
      }

      const count = Number(this.form?.paymentsCount || 1) || 1;
      return Math.max(1, count);
    },
    plannedAmountTotal() {
      return this.currentAmountNumber * this.installmentsCount;
    },
    resolvedRemainingBalance() {
      const explicit = Number(this.remainingBalance);
      if (Number.isFinite(explicit)) {
        return explicit;
      }

      return this.salePriceNumber - this.existingPlannedTotalNumber;
    },
    resolvedMaxAmountPerInstallment() {
      const explicit = Number(this.maxAmountPerInstallment);
      if (Number.isFinite(explicit)) {
        return explicit;
      }

      const remaining = this.resolvedRemainingBalance;
      if (remaining <= 0) {
        return 0;
      }

      return remaining / this.installmentsCount;
    },
    resolvedMaxPercentPerInstallment() {
      if (this.salePriceNumber <= 0) {
        return 0;
      }

      return (this.resolvedMaxAmountPerInstallment / this.salePriceNumber) * 100;
    },
    pendingAmountValue() {
      return this.salePriceNumber - (this.existingPlannedTotalNumber + this.plannedAmountTotal);
    },
    exceededAmountValue() {
      return Math.max(0, -this.pendingAmountValue);
    },
    pendingPercentValue() {
      if (this.salePriceNumber <= 0) {
        return 0;
      }

      return (this.pendingAmountValue / this.salePriceNumber) * 100;
    },
    exceededPercentValue() {
      if (this.salePriceNumber <= 0) {
        return 0;
      }

      return (this.exceededAmountValue / this.salePriceNumber) * 100;
    },
    showPendingWarnings() {
      return this.salePriceNumber > 0;
    },
    hasExceededPending() {
      return this.exceededAmountValue > 0;
    },
    pendingAmountWarning() {
      if (this.exceededAmountValue > 0) {
        return this.text.warnings.exceededAmount({
          amount: this.formatMoney(this.exceededAmountValue),
        });
      }

      return this.text.warnings.pendingAmount({
        pending: this.formatMoney(this.pendingAmountValue),
        maxPerPlan: this.formatMoney(this.resolvedMaxAmountPerInstallment),
      });
    },
    pendingPercentWarning() {
      if (this.exceededAmountValue > 0) {
        return this.text.warnings.exceededPercent({
          percent: this.exceededPercentValue.toFixed(2),
        });
      }

      return this.text.warnings.pendingPercent({
        pendingPercent: this.pendingPercentValue.toFixed(2),
        maxPercent: this.resolvedMaxPercentPerInstallment.toFixed(2),
      });
    },
  },
  watch: {
    'form.amount': {
      immediate: true,
      handler(nextAmount) {
        if (this.percentageSyncLock) {
          return;
        }

        this.syncPercentageFromAmount(nextAmount);
      },
    },
    salePriceNumber(nextSalePrice) {
      if (nextSalePrice <= 0) {
        this.percentageValue = 0;
        return;
      }

      this.syncPercentageFromAmount(this.form?.amount || 0);
    },
    'form.taxType'(nextValue) {
      if (this.isHydrating) {
        return;
      }

      const mode = String(nextValue || '').trim().toLowerCase();
      if (mode === 'group') {
        this.form.taxes = [];
        return;
      }

      this.form.taxGroup = null;
    },
    'form.frequency'(nextValue) {
      applyFrequencyRules(this.form, nextValue, this.firstInstallmentOptions);
    },
    firstInstallmentOptions(nextOptions) {
      if (String(this.form?.frequency || '').trim().toLowerCase() !== 'monthly') {
        return;
      }

      applyFrequencyRules(this.form, 'monthly', nextOptions);
    },
    voucherType: {
      immediate: true,
      handler(nextType) {
        if (this.isHydrating) {
          return;
        }

        applyVoucherTypeRules(this.form, nextType);
      },
    },
    'form.withoutTaxes'(nextValue) {
      if (this.isHydrating) {
        return;
      }

      applyWithoutTaxesRules(this.form, nextValue);
    },
  },
  methods: {
    selectFrequency(frequencyValue) {
      if (!this.canEditGeneralFields) {
        return;
      }

      const normalizedFrequency = String(frequencyValue || 'unique').trim().toLowerCase();
      this.form.frequency = normalizedFrequency;

      // Apply transition synchronously so monthly mode preselects current month immediately.
      applyFrequencyRules(this.form, normalizedFrequency, this.firstInstallmentOptions);
    },
    focusSelectedFirstInstallment() {
      const selectedValue = String(this.form?.firstInstallmentOption?.value || '').trim();
      if (selectedValue === '') {
        return;
      }

      const runOnFrame = typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (callback) => setTimeout(callback, 0);

      this.$nextTick(() => {
        runOnFrame(() => {
          const selectRef = this.$refs.firstInstallmentSelect;
          const selectVm = Array.isArray(selectRef) ? selectRef[0] : selectRef;
          const rootElement = selectVm?.$el || selectVm;

          if (!rootElement || typeof rootElement.querySelector !== 'function') {
            return;
          }

          const contentWrapper = rootElement.querySelector('.multiselect__content-wrapper');
          if (!contentWrapper) {
            return;
          }

          const selectedOption = contentWrapper.querySelector('.multiselect__option--selected');
          if (selectedOption && typeof selectedOption.scrollIntoView === 'function') {
            selectedOption.scrollIntoView({ block: 'nearest' });
          }
        });
      });
    },
    syncPercentageFromAmount(amountValue) {
      const salePrice = this.salePriceNumber;
      if (salePrice <= 0) {
        this.percentageValue = 0;
        return;
      }

      const amount = Number(amountValue || 0) || 0;
      this.percentageValue = Number(((amount / salePrice) * 100).toFixed(4));
    },
    applyPercentageToAmount() {
      if (!this.canEditAmount) {
        return;
      }

      const salePrice = this.salePriceNumber;
      if (salePrice <= 0) {
        return;
      }

      const rawPercentage = Number(this.percentageValue || 0);
      const safePercentage = Number.isFinite(rawPercentage)
        ? Math.max(rawPercentage, 0)
        : 0;

      this.percentageValue = safePercentage;
      this.percentageSyncLock = true;
      this.form.amount = Number(((salePrice * safePercentage) / 100).toFixed(2));
      this.$nextTick(() => {
        this.percentageSyncLock = false;
      });
    },
    toggleWithoutTaxes(event) {
      if (!this.canEditGeneralFields) {
        return;
      }

      const checked = Boolean(event?.target?.checked);
      this.form.withoutTaxes = checked;
      applyWithoutTaxesRules(this.form, checked);
    },
    touchField(fieldName) {
      if (!this.v$?.form || !this.v$.form[fieldName]) {
        return;
      }

      this.v$.form[fieldName].$touch();
    },
    hasFieldError(fieldName) {
      return Boolean(this.v$?.form?.[fieldName]?.$error);
    },
    formatMoney(value) {
      return formatPaymentPlanMoney(value, this.form?.currencyCode);
    },
    formatTaxBreakdownLine(item) {
      return formatPaymentPlanTaxBreakdownLine(item, this.form?.currencyCode);
    },
  },
};
</script>

<style scoped>
:deep(.multiselect.is-invalid .multiselect__tags),
:deep(.multiselect.is-invalid .multiselect__single),
:deep(.multiselect.is-invalid .multiselect__input) {
  border-color: #dc3545 !important;
}

.payment-section-head-copy {
  display: grid;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
}

.payment-section-head-copy p {
  margin: 0;
}

.payment-readonly-status {
  min-height: 44px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 8px;
  background: rgba(248, 250, 252, 0.95);
  padding: 8px 10px;
  display: grid;
  gap: 2px;
  max-width: 340px;
  margin-left: auto;
}

.payment-readonly-status strong {
  font-size: 13px;
  color: #0f172a;
}

.payment-readonly-status small {
  font-size: 11px;
  color: #64748b;
  line-height: 1.2;
}

.expense-field-warning {
  color: #8a95a6;
}

.expense-field-warning--overrun {
  color: #b45309;
}

.payment-frequency-box {
  margin-bottom: 12px;
  display: grid;
  gap: 8px;
}

.payment-frequency-box__label {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
}

.billing-status-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.billing-toggle {
  min-height: 42px;
  border: 1px solid rgba(153, 152, 153, 0.3);
  border-radius: 8px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  margin: 0;
}

.payment-recurring-grid {
  margin-top: 10px;
}

.payment-recurring-count__input {
  height: 38px !important;
  min-height: 38px !important;
  max-height: 38px !important;
  line-height: 1.2;
  padding-top: 6px;
  padding-bottom: 6px;
}

.app-modal__section-head--split {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

@media (max-width: 900px) {
  .app-modal__section-head--split {
    flex-direction: column;
    align-items: stretch;
  }

  .billing-status-row {
    grid-template-columns: 1fr;
  }

  .payment-readonly-status {
    margin-left: 0;
    max-width: none;
  }
}
</style>
